/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ICalloutContentStyleProps, ICalloutContentStyles } from '@fluentui/react/lib/Callout';
import { Coachmark } from '@fluentui/react/lib/Coachmark';
import { ContextualMenu, IContextualMenuItem } from '@fluentui/react/lib/ContextualMenu';
import { IIconProps, Icon } from '@fluentui/react/lib/Icon';
import { ILinkStyleProps, ILinkStyles } from '@fluentui/react/lib/Link';
import { Stack } from '@fluentui/react/lib/Stack';
import { TeachingBubbleContent } from '@fluentui/react/lib/TeachingBubble';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { getFocusStyle, getTheme, mergeStyles } from '@fluentui/style-utilities';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useHistory } from 'react-router';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisText } from 'src/core/components/LuisText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getStylist, joinClassNames } from 'src/core/themes/luisStylist';
import { routeTable } from 'src/core/utils/routeTable';
import { HeaderBreadcrumbItem, HeaderBreadcrumbItemProps } from 'src/modules/subHeader/components/HeaderBreadcrumbItem';

export type DropdownBreadcrumbItemProps = {
    /**
     * Specifies the dropdown menu items.
     */
    dropdownMode: 'customTextAppDropdown' | 'luisAppDropdown' | 'containersDropdown';

    /**
     * The underlying breadcrumb item data.
     */
    item: HeaderBreadcrumbItemProps;
};

const messages = defineMessages({
    upgradedVersionTeachingBubble: {
        id: 'portal.globalSubHeader.upgradedVersionTeachingBubble',
        defaultMessage: 'We’ve created a new version for you. You can roll back to your previous version at any time.'
    },
    manageVersions: {
        id: 'portal.globalSubHeader.manageVersions',
        defaultMessage: 'Manage versions'
    }
});

const { styleComponent, styleDiv, getClassName } = getStylist('GlobalSubHeader');

const focusStyles = mergeStyles(getFocusStyle(getTheme()));
const CoachmarkTarget = styleDiv('CoachmarkTarget', { height: '12px' });
const itemWrapperClassName = getClassName('itemWrapperClassName', { cursor: 'pointer' });
const BreadcrumbChevron = styleComponent<IIconProps>(Icon)('BreadcrumbChevron', theme => ({
    fontSize: theme.iconSize,
    color: theme.fontColor
}));

const linkStyles = classNamesFunction<ILinkStyleProps, ILinkStyles>()({ root: { color: 'white !important' } });
const calloutStyles = classNamesFunction<ICalloutContentStyleProps, ICalloutContentStyles>()({ root: { marginTop: '-3px' } });

/**
 * Renders a special breadcrumb menu item to display
 * the app type and document/conversation app name
 * breadcrumbs with the associated dropdown menus.
 */
export const DropdownBreadcrumbItem = observer((props: DropdownBreadcrumbItemProps) => {
    const { dropdownMode, item } = props;

    const history = useHistory();
    const { localize } = useLocalization();
    const { uiStore, rootStore, dispatcher } = useGlobalState();

    const [isMenuShown, setIsMenuShown] = React.useState(false);
    const coachMarkRef = React.useRef();
    const containerRef = React.useRef();

    let menuItems: IContextualMenuItem[] = [];
    switch (dropdownMode) {
        case 'luisAppDropdown':
            menuItems = rootStore.currentLuisAppStore.appVersionStoreHouse.items.map<IContextualMenuItem>(version => ({
                key: version.id,
                text: `v${version.id}`,
                checked: version.id === rootStore.currentLuisAppStore.activeVersion,
                onClick: () =>
                    dispatcher.dispatch('activateAppVersion', {
                        redirectUrl: `${routeTable.luis.applicationPage(rootStore.currentLuisAppStore.id, version.id)}/`,
                        context: { versionId: version.id }
                    })
            }));
            break;
        default:
    }

    const handleCoachmarkDismissal = () => {
        dispatcher.dispatch('updateCompositeEntitiesMigrationCoachMarkVisibility', { isVisible: false });
        history.push(routeTable.luis.versionsPage(rootStore.currentLuisAppStore.id, rootStore.currentLuisAppStore.activeVersion));
    };

    return (
        <div style={{ height: '100%' }} ref={containerRef}>
            <Stack
                horizontal
                verticalFill
                verticalAlign="center"
                tokens={{ childrenGap: 5 }}
                onClick={() => setIsMenuShown(!isMenuShown)}
                className={joinClassNames(itemWrapperClassName, focusStyles)}>
                <HeaderBreadcrumbItem text={item.text} secondaryText={item.secondaryText} isSelected={item.isSelected} />

                <CoachmarkTarget originalRef={coachMarkRef} />

                <BreadcrumbChevron iconName="ChevronDown" />

                <ContextualMenu
                    gapSpace={3}
                    items={menuItems}
                    hidden={!isMenuShown}
                    target={containerRef}
                    onDismiss={() => setIsMenuShown(false)}
                    calloutProps={{ styles: calloutStyles }}
                />
            </Stack>

            {uiStore.isCompositeEntitiesMigrationCoachMarkVisible && (
                <Coachmark
                    persistentBeak
                    target={coachMarkRef.current}
                    onDismiss={() => dispatcher.dispatch('updateCompositeEntitiesMigrationCoachMarkVisibility', { isVisible: false })}>
                    <TeachingBubbleContent
                        hasCloseButton
                        onDismiss={() => dispatcher.dispatch('updateCompositeEntitiesMigrationCoachMarkVisibility', { isVisible: false })}>
                        <LuisText>
                            {localize(messages.upgradedVersionTeachingBubble)}
                            &nbsp;
                            <ExternalLink isUnderlined styles={linkStyles} onClick={handleCoachmarkDismissal}>
                                {localize(messages.manageVersions)}
                            </ExternalLink>
                        </LuisText>
                    </TeachingBubbleContent>
                </Coachmark>
            )}
        </div>
    );
});
